import { useQuery } from '@tanstack/react-query';
import { useDataProvider } from 'react-admin';
import {
  buildTrackingTraceView,
  type TrackingTraceView,
} from './trackingDiagnosticsModel';
import type { Click, Merchant, Transaction, User } from '../dataProvider/types';

/**
 * The Tracking diagnostics trace, read through the aggregation seam
 * (`buildTrackingTraceView`), NOT computed in the component. For v1 it pulls the member,
 * their clicks and transactions, and the merchants (for names) from the mock provider and
 * aggregates client-side; the real-data migration replaces the fetch-and-aggregate here
 * with a server-side endpoint and the surface does not change. Same seam idiom as
 * useFraudQueue.
 *
 * READ-ONLY: this only ever reads. The injected `now` drives the overdue reading; the
 * optional `focusTransactionId` is the per-transaction entry point. Returns null until the
 * member resolves (the surface guards on that and on the region scope).
 */

const ALL = {
  pagination: { page: 1, perPage: 100_000 },
  sort: { field: 'id', order: 'ASC' as const },
};

export const useTrackingDiagnostics = (
  userId: number,
  now: number,
  focusTransactionId: number | null,
): {
  view: TrackingTraceView | null;
  isLoading: boolean;
} => {
  const dataProvider = useDataProvider();

  const { data, isLoading } = useQuery({
    queryKey: ['tracking-diagnostics', userId, now, focusTransactionId],
    queryFn: async () => {
      const [user, clicks, transactions, merchants] = await Promise.all([
        dataProvider.getOne('users', { id: userId }),
        dataProvider.getList('clicks', { ...ALL, filter: { userId } }),
        dataProvider.getList('transactions', { ...ALL, filter: { userId } }),
        dataProvider.getList('merchants', { ...ALL, filter: {} }),
      ]);
      return buildTrackingTraceView(
        {
          user: user.data as unknown as User,
          clicks: clicks.data as unknown as Click[],
          transactions: transactions.data as unknown as Transaction[],
          merchants: merchants.data as unknown as Merchant[],
        },
        { now, focusTransactionId },
      );
    },
  });

  return { view: data ?? null, isLoading };
};
